import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { 
  Copy, 
  Trash2, 
  RefreshCw, 
  Plus, 
  Key, 
  Edit,
  AlertCircle,
  CheckCircle,
  Loader2,
  Eye,
  EyeOff,
  HelpCircle
} from 'lucide-react';

const UserApiKeyManager = ({ onShowHelp }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  const [copySuccess, setCopySuccess] = useState(null);
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    usage_limit: '',
    claude_system_prompt_override: '',
    webhook_url: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    usage_limit: 1000,
    claude_system_prompt_override: '',
    webhook_url: ''
  });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(getApiUrl('/user/api-keys-management'), {
        withCredentials: true
      });
      
      if (response.data && response.data.api_keys) {
        setApiKeys(response.data.api_keys);
        console.log(`✅ Fetched ${response.data.count} API keys for user`);
      } else {
        setApiKeys([]);
        console.log('✅ No API keys found for user');
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to fetch API keys');
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post(getApiUrl('/user/api-keys-management'), formData, {
        withCredentials: true
      });
      
      if (response.status === 201) {
        setSuccess('API key created successfully!');
        setShowCreateForm(false);
        setFormData({
          name: '',
          usage_limit: 1000,
          claude_system_prompt_override: '',
          webhook_url: ''
        });
        fetchApiKeys(); // Refresh the list
      }
    } catch (err) {
      console.error('Error creating API key:', err);
      setError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateApiKey = async (customerId) => {
    if (!window.confirm('Are you sure you want to regenerate the API key? This will invalidate the current key.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post(getApiUrl(`/user/my-api-keys/${customerId}/regenerate`), {}, {
        withCredentials: true
      });
      
      setApiKeys(apiKeys.map(apiKey => 
        apiKey.id === customerId ? response.data.customer : apiKey
      ));
      setSuccess('API key regenerated successfully!');
    } catch (err) {
      console.error('Error regenerating API key:', err);
      setError(err.response?.data?.error || 'Failed to regenerate API key');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApiKey = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await axios.delete(getApiUrl(`/user/my-api-keys/${customerId}`), {
        withCredentials: true
      });
      
      setApiKeys(apiKeys.filter(apiKey => apiKey.id !== customerId));
      setSuccess('API key deleted successfully!');
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError(err.response?.data?.error || 'Failed to delete API key');
    } finally {
      setLoading(false);
    }
  };

  const handleEditApiKey = (apiKey) => {
    setEditingApiKey(apiKey);
    setEditFormData({
      name: apiKey.name,
      usage_limit: apiKey.usage_limit || '',
      claude_system_prompt_override: apiKey.claude_system_prompt_override || '',
      webhook_url: apiKey.webhook_url || ''
    });
  };

  const handleUpdateApiKey = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.put(getApiUrl(`/user/my-api-keys/${editingApiKey.id}`), editFormData, {
        withCredentials: true
      });
      
      setApiKeys(apiKeys.map(apiKey => 
        apiKey.id === editingApiKey.id ? response.data.customer : apiKey
      ));
      setEditingApiKey(null);
      setEditFormData({ name: '', usage_limit: '', claude_system_prompt_override: '', webhook_url: '' });
      setSuccess('API key updated successfully!');
    } catch (err) {
      console.error('Error updating API key:', err);
      setError(err.response?.data?.error || 'Failed to update API key');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingApiKey(null);
    setEditFormData({ name: '', usage_limit: '', claude_system_prompt_override: '', webhook_url: '' });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('API key copied!');
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('Failed to copy');
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  const toggleApiKeyVisibility = (customerId) => {
    setShowApiKey(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  if (loading && apiKeys.length === 0) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
          <span className="ml-3 text-gray-300">Loading your API keys...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">My API Keys</h1>
            <p className="text-gray-400">Create and manage your API keys for agent integration</p>
          </div>
          <div className="flex space-x-3">
            {onShowHelp && (
              <button
                onClick={onShowHelp}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Help</span>
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center space-x-2 bg-[#7B61FF] text-white px-4 py-2 rounded-xl hover:bg-[#6B51EF] transition-colors duration-300"
            >
              <Plus className="w-4 h-4" />
              <span>Create API Key</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900 border border-red-700 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-green-900 border border-green-700 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
            <span className="text-green-300">{success}</span>
          </div>
        </div>
      )}

      {/* Copy Success Message */}
      {copySuccess && (
        <div className="bg-blue-900 border border-blue-700 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-blue-400 mr-2" />
            <span className="text-blue-300">{copySuccess}</span>
          </div>
        </div>
      )}

      {/* Create API Key Form */}
      {showCreateForm && (
        <div className="bg-gray-800 border border-gray-600 rounded-xl p-6 mb-6" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Create New API Key</h3>
          <form onSubmit={handleCreateApiKey}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                  placeholder="Enter customer name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Usage Limit
                </label>
                <input
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                  placeholder="1000"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Claude System Prompt Override
              </label>
              <textarea
                value={formData.claude_system_prompt_override}
                onChange={(e) => setFormData({ ...formData, claude_system_prompt_override: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                placeholder="Optional custom system prompt for Claude evaluations"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Webhook URL
              </label>
              <input
                type="url"
                value={formData.webhook_url}
                onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                placeholder="https://your-webhook-url.com/endpoint"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-[#7B61FF] text-white rounded-xl hover:bg-[#6B51EF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                <span>{loading ? 'Creating...' : 'Create API Key'}</span>
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="text-center py-12">
          <Key className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No API Keys Yet</h3>
          <p className="text-gray-500 mb-6">Create your first API key to start integrating agents</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-[#7B61FF] text-white px-6 py-3 rounded-xl hover:bg-[#6B51EF] transition-colors duration-300 mx-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Create Your First API Key</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((apiKey) => (
            <div key={apiKey.id} className="bg-gray-800 border border-gray-600 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                              <h3 className="text-lg font-semibold text-white">{apiKey.name}</h3>
            <p className="text-sm text-gray-400">Created {new Date(apiKey.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditApiKey(apiKey)}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleRegenerateApiKey(apiKey.id)}
                    className="p-2 text-yellow-400 hover:text-yellow-300 transition-colors"
                    title="Regenerate API Key"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteApiKey(apiKey.id)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors"
                    title="Delete API Key"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* API Key Display */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-300 mb-2">API Key</label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleApiKeyVisibility(apiKey.id)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {showApiKey[apiKey.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(apiKey.api_key)}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                      title="Copy API Key"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="bg-gray-900 border border-gray-600 rounded-lg p-3">
                  <code className="text-sm text-gray-300 break-all">
                    {showApiKey[apiKey.id] ? apiKey.api_key : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'}
                  </code>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-900 rounded-lg p-3">
                                  <div className="text-sm text-gray-400">Usage</div>
                <div className="text-lg font-semibold text-white">
                    {apiKey.usage_count || 0} / {apiKey.usage_limit || '∞'}
                  </div>
                </div>
                <div className="bg-gray-900 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Status</div>
                  <div className="text-lg font-semibold text-green-400">Active</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-3">
                                  <div className="text-sm text-gray-400">Webhook</div>
                <div className="text-sm text-gray-300">
                    {apiKey.webhook_url ? 'Configured' : 'Not set'}
                  </div>
                </div>
              </div>

              {/* Usage Instructions */}
              <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-300 mb-2">Usage Instructions</h4>
                <p className="text-sm text-blue-200 mb-2">
                  Include this API key in the <code className="bg-blue-900/50 px-1 rounded">x-api-key</code> header with your requests:
                </p>
                <div className="bg-gray-900 rounded p-2">
                  <code className="text-xs text-gray-300">
                    {`curl -X POST https://your-backend.com/agent/register
  -H "x-api-key: ${apiKey.api_key.substring(0, 20)}..."
  -H "Content-Type: application/json"
  -d '{"agent_name": "MyAgent", ...}'`}
                  </code>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingApiKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Edit API Key Settings</h3>
            <form onSubmit={handleUpdateApiKey}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Usage Limit</label>
                  <input
                    type="number"
                    value={editFormData.usage_limit}
                    onChange={(e) => setEditFormData({ ...editFormData, usage_limit: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Claude System Prompt Override</label>
                <textarea
                  value={editFormData.claude_system_prompt_override}
                  onChange={(e) => setEditFormData({ ...editFormData, claude_system_prompt_override: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Webhook URL</label>
                <input
                  type="url"
                  value={editFormData.webhook_url}
                  onChange={(e) => setEditFormData({ ...editFormData, webhook_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-[#7B61FF] text-white rounded-xl hover:bg-[#6B51EF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span>{loading ? 'Updating...' : 'Update Settings'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserApiKeyManager; 